import { ValidatorFn } from '@angular/forms';

/**
 * Parsing and validation for standard five-field cron expressions:
 * `minute hour day-of-month month day-of-week`.
 */

const ANY = '*';

interface CronField {
  min: number;
  max: number;
  /** Accepted three-letter aliases, indexed by their numeric value. */
  names?: string[];
}

const MONTH_NAMES = ['', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const MONTH_FULL_NAMES = [
  '',
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The five standard cron fields, in order: minute, hour, day of month, month, day of week.
 * Day of week accepts 7 as well as 0 for Sunday, as standard cron does.
 */
const CRON_FIELDS: CronField[] = [
  { min: 0, max: 59 },
  { min: 0, max: 23 },
  { min: 1, max: 31 },
  { min: 1, max: 12, names: MONTH_NAMES },
  { min: 0, max: 7, names: DAY_NAMES }
];

/** Shown for an expression that is valid but has no natural reading, so the summary is never silent. */
const NO_SUMMARY = 'Valid schedule — no plain-English summary available.';

/** How many values of one field are worth spelling out before a summary stops being readable. */
const MAX_LISTED_VALUES = 4;

/** How many clock times are worth spelling out. */
const MAX_LISTED_TIMES = 6;

/**
 * Whether a hand-written cron expression is well formed: five fields of `*`, a number, an `a-b` range
 * or a `/step`, optionally comma-separated, with month and day-of-week also accepting their usual
 * three-letter names. Values are range-checked per field.
 */
export function isValidCron(expression: string): boolean {
  const fields = expression.trim().split(/\s+/);

  return (
    fields.length === CRON_FIELDS.length && fields.every((field, index) => isValidField(field, CRON_FIELDS[index]))
  );
}

/**
 * Plain-English summary of a cron expression, for confirming what was typed. Covers every operator the
 * field accepts — values, lists, ranges and steps — by reading the time of day and the days it runs on
 * separately, then joining them. Returns {@link NO_SUMMARY} for the few combinations that have no short
 * reading, and an empty string only when the expression is not valid cron at all.
 */
export function describeCron(expression: string): string {
  if (!isValidCron(expression)) return '';

  const [minute, hour, dayOfMonth, month, dayOfWeek] = expression.trim().split(/\s+/);
  const time = describeTime(minute, hour);
  const days = describeDays(dayOfMonth, month, dayOfWeek);
  if (!time || !days) return NO_SUMMARY;

  // A clock time reads after the days it runs on, a frequency reads before them.
  return time.kind === 'clock' ? phrase(days.lead, time.text, days.suffix) : phrase(time.text, days.tail, days.suffix);
}

/** Rejects a malformed cron expression. An empty field is left to `Validators.required`. */
export const cronExpressionValidator: ValidatorFn = control => {
  const expression = (control.value as string)?.trim();
  if (!expression) return null;

  return isValidCron(expression) ? null : { invalidCron: true };
};

function isValidField(field: string, { min, max, names }: CronField): boolean {
  return field.split(',').every(item => {
    const [range, step, ...extra] = item.split('/');
    if (extra.length || (step !== undefined && !isNumberWithin(step, 1, max))) return false;
    if (range === ANY) return true;

    const [start, end, ...rest] = range.split('-');
    if (rest.length || !isNumberWithin(start, min, max, names)) return false;

    return (
      end === undefined || (isNumberWithin(end, min, max, names) && toNumber(end, names) >= toNumber(start, names))
    );
  });
}

function isNumberWithin(value: string, min: number, max: number, names?: string[]): boolean {
  const parsed = toNumber(value, names);
  return !Number.isNaN(parsed) && parsed >= min && parsed <= max;
}

function toNumber(value: string, names?: string[]): number {
  const named = names?.indexOf(value.toUpperCase()) ?? -1;
  if (named >= 0) return named;

  return /^\d+$/.test(value) ? Number.parseInt(value, 10) : Number.NaN;
}

/** One comma-separated item of a field: `*`, a value, an `a-b` range, or any of those with a `/step`. */
interface CronItem {
  /** Undefined for `*`. */
  start?: number;
  /** Set only for an `a-b` range. */
  end?: number;
  step?: number;
}

/** Splits an already-validated field into its items. */
function parseField(field: string, names?: string[]): CronItem[] {
  return field.split(',').map(item => {
    const [range, step] = item.split('/');
    const parsed: CronItem = step === undefined ? {} : { step: Number.parseInt(step, 10) };
    if (range === ANY) return parsed;

    const [start, end] = range.split('-');
    parsed.start = toNumber(start, names);
    if (end !== undefined) parsed.end = toNumber(end, names);

    return parsed;
  });
}

/** Whether the field is a bare `*`. */
function isAny(items: CronItem[]): boolean {
  return items.length === 1 && items[0].start === undefined && items[0].step === undefined;
}

/** The `n` of a step written against the whole field, as in `*` over 15, which sets how often it fires. */
function stepOfAny(items: CronItem[]): number | undefined {
  const [item] = items;
  return items.length === 1 && item.start === undefined ? item.step : undefined;
}

/** Every value the field matches, ranges expanded; undefined for `*`, for steps, or past `limit` values. */
function valuesOf(items: CronItem[], limit: number): number[] | undefined {
  const values: number[] = [];

  for (const { start, end, step } of items) {
    if (start === undefined || step !== undefined) return undefined;

    for (let value = start; value <= (end ?? start); value++) {
      if (values.length >= limit) return undefined;
      values.push(value);
    }
  }

  return values;
}

/** A single `a-b` range with no step, which reads as a window rather than as a list. */
function rangeOf(items: CronItem[]): { start: number; end: number } | undefined {
  const [item] = items;

  return items.length === 1 && item.start !== undefined && item.end !== undefined && item.step === undefined
    ? { start: item.start, end: item.end }
    : undefined;
}

/**
 * A reading of the minute and hour fields, either as clock times (`at 9:00 AM`) or as a frequency
 * (`Every 15 minutes`), which the two halves of the summary are then ordered around.
 */
interface TimePhrase {
  text: string;
  kind: 'clock' | 'frequency';
}

function describeTime(minuteField: string, hourField: string): TimePhrase | undefined {
  const minutes = parseField(minuteField);
  const hours = parseField(hourField);

  const hourRange = rangeOf(hours);
  const window = hourRange && `from ${formatTime(hourRange.start, 0)} to ${formatTime(hourRange.end, 0)}`;
  const minuteStep = stepOfAny(minutes);

  // A `*` or stepped minute field makes the schedule sub-hourly; the hour field only narrows the window.
  if (isAny(minutes) || minuteStep !== undefined) {
    const every = minuteStep === undefined ? 'Every minute' : `Every ${minuteStep} minutes`;
    if (isAny(hours)) return { text: every, kind: 'frequency' };

    return window ? { text: `${every} ${window}`, kind: 'frequency' } : undefined;
  }

  const minuteValues = valuesOf(minutes, MAX_LISTED_VALUES);
  if (!minuteValues?.length) return undefined;

  // Anything but a fixed set of hours runs hourly, at whichever minute past the hour the field pins.
  const hourStep = stepOfAny(hours);
  if (isAny(hours) || hourStep !== undefined || window) {
    if (minuteValues.length > 1) return undefined;

    const past = minuteValues[0] === 0 ? '' : ` at :${minuteValues[0].toString().padStart(2, '0')}`;
    if (hourStep !== undefined) return { text: `Every ${hourStep} hours${past}`, kind: 'frequency' };

    return { text: window ? `Hourly ${window}${past}` : `Hourly${past}`, kind: 'frequency' };
  }

  const hourValues = valuesOf(hours, MAX_LISTED_VALUES);
  if (!hourValues?.length) return undefined;

  const times = hourValues.flatMap(hour => minuteValues.map(minute => formatTime(hour, minute)));

  return times.length <= MAX_LISTED_TIMES ? { text: `at ${joinList(times)}`, kind: 'clock' } : undefined;
}

/**
 * A reading of the two day fields and the month field, in both the sentence-leading form used before a
 * clock time (`Every weekday`) and the trailing form used after a frequency (`on weekdays`).
 */
interface DayPhrase {
  lead: string;
  tail: string;
  /** The months the schedule is limited to, e.g. `in January`, and empty when it runs all year. */
  suffix: string;
}

function describeDays(dayOfMonthField: string, monthField: string, dayOfWeekField: string): DayPhrase | undefined {
  const dayOfMonth = parseField(dayOfMonthField);
  const month = parseField(monthField, MONTH_NAMES);
  const dayOfWeek = parseField(dayOfWeekField, DAY_NAMES);

  // Cron runs a job when *either* day field matches, and that has no short unambiguous reading.
  if (!isAny(dayOfMonth) && !isAny(dayOfWeek)) return undefined;

  const months = isAny(month) ? [] : valuesOf(month, MONTH_FULL_NAMES.length)?.map(value => MONTH_FULL_NAMES[value]);
  if (!months) return undefined;

  const suffix = months.length ? `in ${joinList(months)}` : '';

  if (!isAny(dayOfWeek)) {
    const weekdays = describeWeekdays(dayOfWeek);
    return weekdays && { ...weekdays, suffix };
  }

  if (!isAny(dayOfMonth)) {
    const dates = valuesOf(dayOfMonth, MAX_LISTED_VALUES);
    if (!dates?.length) return undefined;

    const ordinals = joinList(dates.map(ordinal));

    // `Monthly` would contradict a schedule already limited to named months.
    return months.length
      ? { lead: `On the ${ordinals}`, tail: `on the ${ordinals}`, suffix }
      : { lead: `Monthly on the ${ordinals}`, tail: `on the ${ordinals} of each month`, suffix };
  }

  return { lead: months.length ? 'Every day' : 'Daily', tail: '', suffix };
}

function describeWeekdays(items: CronItem[]): Omit<DayPhrase, 'suffix'> | undefined {
  // Both 0 and 7 mean Sunday, so fold them together before reading the set.
  const values = valuesOf(items, WEEKDAY_NAMES.length + 1)?.map(value => value % 7);
  if (!values?.length) return undefined;

  const unique = [...new Set(values)].sort((first, second) => first - second);
  const key = unique.join();
  if (key === '0,1,2,3,4,5,6') return { lead: 'Daily', tail: '' };
  if (key === '1,2,3,4,5') return { lead: 'Every weekday', tail: 'on weekdays' };
  if (key === '0,6') return { lead: 'Every weekend', tail: 'on weekends' };
  if (unique.length > MAX_LISTED_VALUES) return undefined;

  const names = unique.map(value => WEEKDAY_NAMES[value]);

  return { lead: `Every ${joinList(names)}`, tail: `on ${joinList(names.map(name => `${name}s`))}` };
}

/** Joins the non-empty parts of a summary with single spaces. */
function phrase(...parts: string[]): string {
  return parts.filter(Boolean).join(' ');
}

/** `a`, `a and b`, `a, b and c`. */
function joinList(items: string[]): string {
  if (items.length < 2) return items.join('');

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function ordinal(day: number): string {
  const lastDigit = day % 10;
  const lastTwoDigits = day % 100;
  if (lastDigit === 1 && lastTwoDigits !== 11) return `${day}st`;
  if (lastDigit === 2 && lastTwoDigits !== 12) return `${day}nd`;
  if (lastDigit === 3 && lastTwoDigits !== 13) return `${day}rd`;

  return `${day}th`;
}

function formatTime(hours: number, minutes: number): string {
  const period = hours < 12 ? 'AM' : 'PM';
  return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${period}`;
}
